import React, { useRef, useState, useEffect, useMemo } from "react";
import Icon from "../../../components/AppIcon";

// Format seconds into MM:SS format
const formatTime = (secs) => {
  if (isNaN(secs) || secs <= 0) return "00:00";
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60);
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const JogWheel = ({ id, isPlaying, bpm, playbackRate, currentTime = 0, duration = 0, onScratch, onReleaseScratch, color = "primary" }) => {
  const wheelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [localTime, setLocalTime] = useState(currentTime);
  const prevAngle = useRef(0);
  const lastTime = useRef(0);

  // Sync prop changes to local state
  useEffect(() => {
    setLocalTime(currentTime);
  }, [currentTime]);

  // Reset platter rotation when song is stopped/ended at 0
  useEffect(() => {
    if (!isPlaying && localTime === 0) {
      setRotation(0);
    }
  }, [isPlaying, localTime]);

  // High-performance requestAnimationFrame loop to poll play time
  useEffect(() => {
    let animId = null;

    const pollTime = () => {
      if (window.djEngine) {
        const deck = window.djEngine.decks.get(id);
        if (deck) {
          const t = window.djEngine.getDeckCurrentTime(id);
          setLocalTime(t);
        }
      }
      animId = requestAnimationFrame(pollTime);
    };

    animId = requestAnimationFrame(pollTime);
    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [id]);

  const colors = useMemo(() => {
    const themeColors = {
      primary: { // Hacker Green / Matrix style
        waveBass: "#14532d",   // Dark forest green
        waveMid: "#00ff41",    // Matrix neon green
        waveHigh: "#86efac",   // Light emerald green
        playhead: "#00ff41",
        loopBg: "rgba(0, 255, 65, 0.12)",
        loopBorder: "#00ff41"
      },
      cyan: { // Cyber Cyan
        waveBass: "#0f3a44",   // Dark slate teal
        waveMid: "#00ffcc",    // Neon cyan
        waveHigh: "#a5f3fc",   // Ice blue
        playhead: "#00ffcc",
        loopBg: "rgba(0, 255, 204, 0.12)",
        loopBorder: "#00ffcc"
      },
      blue: { // Digital Blue
        waveBass: "#1e1b4b",   // Midnight blue
        waveMid: "#3b82f6",    // Electric blue
        waveHigh: "#93c5fd",   // Sky blue
        playhead: "#3b82f6",
        loopBg: "rgba(59, 130, 246, 0.12)",
        loopBorder: "#3b82f6"
      },
      warning: { // Amber Warning
        waveBass: "#451a03",   // Deep rust
        waveMid: "#f59e0b",    // Neon amber
        waveHigh: "#fde047",   // Neon yellow
        playhead: "#f59e0b",
        loopBg: "rgba(245, 158, 11, 0.12)",
        loopBorder: "#f59e0b"
      }
    };
    return themeColors[color] || themeColors.primary;
  }, [color]);

  // Constants for theme colors
  const colorMap = {
    primary: "text-primary border-primary/20 bg-primary/5 shadow-terminal-glow",
    cyan: "text-terminal-cyan border-terminal-cyan/20 bg-terminal-cyan/5",
    blue: "text-terminal-blue border-terminal-blue/20 bg-terminal-blue/5",
    warning: "text-warning border-warning/20 bg-warning/5"
  };

  // Jog wheel rotation animation when playing
  useEffect(() => {
    if (!isPlaying || isDragging) return;

    let frameId;
    const speed = (bpm / 60) * 360 * playbackRate; // degrees per second
    let lastStamp = performance.now();

    const animate = (timestamp) => {
      const deltaSec = (timestamp - lastStamp) / 1000;
      lastStamp = timestamp;
      setRotation(prev => (prev + speed * deltaSec) % 360);
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isPlaying, bpm, playbackRate, isDragging]);

  const getMouseCoords = (e) => {
    if (!wheelRef.current) return { x: 0, y: 0 };
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Support mouse and touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: clientX - centerX,
      y: clientY - centerY
    };
  };

  const handleStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const coords = getMouseCoords(e);
    prevAngle.current = Math.atan2(coords.y, coords.x);
    lastTime.current = performance.now();
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const coords = getMouseCoords(e);
    const currentAngle = Math.atan2(coords.y, coords.x);
    
    // Calculate angle difference (-PI to PI)
    let angleDiff = currentAngle - prevAngle.current;
    if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

    const stamp = performance.now();
    const timeDiff = Math.max(1, stamp - lastTime.current); // avoid division by zero
    
    // Calculate speed factor based on angle delta over time
    const speedFactor = (angleDiff / timeDiff) * 350; // scaling multiplier

    // Call scratch callback
    if (onScratch) {
      onScratch(speedFactor);
    }

    // Accumulate rotation visually
    setRotation(prev => (prev + (angleDiff * 180) / Math.PI) % 360);

    // Save state for next move
    prevAngle.current = currentAngle;
    lastTime.current = stamp;
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (onReleaseScratch) {
      onReleaseScratch();
    }
  };

  // Add global touch listeners when scratching to handle drags smoothly
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchend", handleEnd);
      window.addEventListener("mousemove", handleMove, { passive: false });
      window.addEventListener("touchmove", handleMove, { passive: false });
    }
    return () => {
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchend", handleEnd);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("touchmove", handleMove);
    };
  }, [isDragging]);

  // Calculate circular progress percentage (270 degree arc gauge)
  // pathLength is set to 100. The active gauge spans exactly 75 units (75% of circle).
  const progressPct = duration > 0 ? (localTime / duration) : 0;
  const activeStroke = 75 * progressPct;

  return (
    <div className="flex flex-col items-center select-none">
      {/* Platter outer rim */}
      <div
        ref={wheelRef}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        className={`w-40 h-40 sm:w-44 sm:h-44 md:w-48 md:h-48 rounded-full border-4 flex items-center justify-center relative cursor-grab active:cursor-grabbing transition-shadow duration-300 ${colorMap[color]}`}
        style={{ touchAction: "none" }}
      >
        {/* Vinyl grooved circles */}
        <div className="absolute inset-2 rounded-full border border-zinc-800/80 pointer-events-none"></div>
        <div className="absolute inset-4 rounded-full border border-zinc-800/60 pointer-events-none"></div>
        <div className="absolute inset-6 rounded-full border border-zinc-800/40 pointer-events-none"></div>
        <div className="absolute inset-8 rounded-full border border-zinc-800/25 pointer-events-none"></div>
        <div className="absolute inset-10 rounded-full border border-zinc-900 pointer-events-none"></div>

        {/* Rotating Slipmat Label (Vinyl decal only) */}
        <div
          className="absolute w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-zinc-900 shadow-inner border border-zinc-800 pointer-events-none"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {/* Slipmat graphic lines */}
          <div className="absolute w-full h-0.5 bg-zinc-800/40 top-1/2 left-0 -translate-y-1/2"></div>
          <div className="absolute w-0.5 h-full bg-zinc-800/40 left-1/2 top-0 -translate-x-1/2"></div>
          
          {/* Neon marker for scratch playhead tracking */}
          <div className={`absolute w-3 h-3 rounded-full top-1 left-1/2 -translate-x-1/2 bg-current ${color === 'primary' ? 'text-[#00ff41]' : color === 'cyan' ? 'text-terminal-cyan' : color === 'blue' ? 'text-terminal-blue' : 'text-warning'}`}></div>
        </div>

        {/* Stationary Center LCD Display (Remains stationary CDJ-style) */}
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-zinc-950/95 border border-zinc-800/90 flex flex-col items-center justify-center shadow-lg z-10 text-center font-fira select-none pointer-events-none">
          <span className="text-[7.5px] text-zinc-500 uppercase tracking-widest scale-90 leading-none">BPM</span>
          <span className="text-[11px] font-bold text-foreground leading-none mt-0.5">{bpm}</span>
          <span className={`text-[8.5px] font-bold mt-1.5 leading-none ${color === 'primary' ? 'text-[#00ff41]' : color === 'cyan' ? 'text-[#00ffcc]' : color === 'blue' ? 'text-[#3b82f6]' : 'text-[#f59e0b]'}`}>
            {formatTime(localTime)}
          </span>
        </div>

        {/* LED Open Arc Ring Display (Circular Track Progress Bar starting bottom-left and ending bottom-right) */}
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none" 
          viewBox="0 0 100 100"
          style={{ transform: "rotate(135deg)" }}
        >
          {/* Background track circle gauge (open 270 degree arc) */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={colors.waveMid}
            opacity="0.12"
            strokeWidth="2.5"
            pathLength="100"
            strokeDasharray="75 100"
            strokeDashoffset="0"
          />
          {/* Active progress circle gauge (grows clockwise dynamically, transition-free for 60fps render accuracy) */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={colors.waveMid}
            strokeWidth="3"
            pathLength="100"
            strokeDasharray={`${activeStroke} 100`}
            strokeDashoffset="0"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* BPM / Info readouts below wheel */}
      <div className="mt-3 font-fira text-center">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Speed Rate</div>
        <div className="text-xs font-bold text-foreground">
          <span>{playbackRate >= 1.0 ? `+${((playbackRate - 1) * 100).toFixed(1)}%` : `${((playbackRate - 1) * 100).toFixed(1)}%`}</span>
        </div>
      </div>
    </div>
  );
};

export default JogWheel;
